/**
 * Página de Información Institucional - Portal de Transparencia
 * Estilo Awwwards Interior - Información general y corporativa
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información Institucional con estilo Awwwards
 * @returns {JSX.Element} Página de información institucional
 */
const InstitutionalInfoPage = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <Link 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">Portal de Transparencia</span>
            </Link>
            
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              Información<br />
              <span className="text-intercasa-yellow">de Contratos</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              Contratos formalizados con entidades públicas
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
          
            {/* Encabezado de sección */}
            <AnimatedSection animation="fadeUp" className="mb-12">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                Ejercicio 2024
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl">
                Contratos formalizados con entidades públicas
              </h2>
            </AnimatedSection>

            {/* Grid de secciones */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              
              {/* Contratos formalizados */}
              <AnimatedSection animation="fadeUp" delay={0.05}>
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-intercasa-red/10 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-intercasa-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="font-baskerville text-lg font-bold text-intercasa-dark">
                      Contratos Formalizados
                    </h3>
                  </div>
                  <p className="text-intercasa-dark/70 leading-relaxed text-sm">
                    La sociedad INTERCASA, S.A. no ha resultado adjudicataria de ningún contrato con la administración pública, organismos públicos, entidades de derecho público vinculados o dependientes ni ninguna universidad pública durante el ejercicio 2024. Igualmente se deja constancia de que no se han producido modificaciones en el período evaluado.
                  </p>
                </div>
              </AnimatedSection>

              {/* Datos estadísticos */}
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-intercasa-red/10 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-intercasa-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="font-baskerville text-lg font-bold text-intercasa-dark">
                      Datos Estadísticos
                    </h3>
                  </div>
                  <p className="text-intercasa-dark/70 leading-relaxed text-sm">
                    La sociedad INTERCASA, S.A. no ha resultado adjudicataria de ningún contrato con la administración pública, organismos públicos, entidades de derecho público vinculados o dependientes ni ninguna universidad pública durante el ejercicio 2024. Igualmente se deja constancia de que no se han producido modificaciones en el período evaluado.
                  </p>
                </div>
              </AnimatedSection>

              {/* Modificaciones */}
              <AnimatedSection animation="fadeUp" delay={0.15}>
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-intercasa-red/10 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-intercasa-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                    <h3 className="font-baskerville text-lg font-bold text-intercasa-dark">
                      Modificaciones
                    </h3>
                  </div>
                  <p className="text-intercasa-dark/70 leading-relaxed text-sm">
                    La sociedad INTERCASA, S.A. no ha resultado adjudicataria de ningún contrato con la administración pública, organismos públicos, entidades de derecho público vinculados o dependientes ni ninguna universidad pública durante el ejercicio 2024. Igualmente se deja constancia de que no se han producido modificaciones en el período evaluado.
                  </p>
                </div>
              </AnimatedSection>

              {/* Decisiones de Desistimiento y Renuncia */}
              <AnimatedSection animation="fadeUp" delay={0.2}>
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-intercasa-red/10 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-intercasa-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <h3 className="font-baskerville text-lg font-bold text-intercasa-dark">
                      Desistimiento y Renuncia
                    </h3>
                  </div>
                  <p className="text-intercasa-dark/70 leading-relaxed text-sm">
                    La sociedad INTERCASA, S.A. no ha resultado adjudicataria de ningún contrato con la administración pública, organismos públicos, entidades de derecho público vinculados o dependientes ni ninguna universidad pública durante el ejercicio 2024. Igualmente se deja constancia de que no se han producido modificaciones en el período evaluado.
                  </p>
                </div>
              </AnimatedSection>
            </div>

            {/* Contratos Menores - Card destacada */}
            <AnimatedSection animation="fadeUp" delay={0.25} className="mb-12">
              <div className="bg-intercasa-dark rounded-2xl p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-intercasa-red rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="font-baskerville text-xl font-bold text-white">
                    Relación de Contratos Menores
                  </h3>
                </div>
                <p className="text-white/70 leading-relaxed">
                  La sociedad INTERCASA, S.A. no ha resultado adjudicataria de ningún contrato menor con la administración pública, organismos públicos, entidades de derecho público vinculados o dependientes ni ninguna universidad pública durante el ejercicio 2024. Igualmente se deja constancia de que no se han producido modificaciones en el período evaluado.
                </p>
              </div>
            </AnimatedSection>

            {/* Botones de descarga */}
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div className="flex flex-wrap gap-4 justify-center">
                <a 
                  href="/PortalTansparenciaIntercasa/Contratos/Contratos.pdf"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white font-lato font-medium rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar PDF
                </a>
                <a 
                  href="/PortalTansparenciaIntercasa/Contratos/Contratos.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white font-lato font-medium rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 shadow-md hover:shadow-lg"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Descargar ODT
                </a>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default InstitutionalInfoPage;